import { ClerkProvider } from '@clerk/clerk-react';
import { dark } from '@clerk/themes';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { RouterProvider } from 'react-router-dom';
import { Toaster } from 'sonner';
import { router } from '@/router';
import { env } from '@/lib/env';
import { ImpersonationProvider } from '@/hooks/useImpersonation';
import { ThemeProvider, useTheme, type ResolvedTheme } from '@/hooks/useTheme';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
});

/**
 * Clerk's widgets (org switcher, user button) follow the portal theme so
 * they read as part of the product, not a third-party widget glued on.
 */
const FONT = '"Manrope", ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, system-ui, sans-serif';

function clerkAppearance(theme: ResolvedTheme) {
  return theme === 'dark'
    ? {
        baseTheme: dark,
        variables: {
          colorPrimary: '#8EA2FF',
          colorBackground: '#111319',
          colorInputBackground: '#14161C',
          colorInputText: '#EEF0F5',
          colorText: '#EEF0F5',
          colorTextSecondary: '#8C93A5',
          colorNeutral: '#8C93A5',
          borderRadius: '0.625rem',
          fontFamily: FONT,
        },
      }
    : {
        variables: {
          colorPrimary: '#4A5BEA',
          colorBackground: '#FFFFFF',
          colorInputBackground: '#FFFFFF',
          colorInputText: '#0B0D12',
          colorText: '#0B0D12',
          colorTextSecondary: '#5B6273',
          colorNeutral: '#5B6273',
          borderRadius: '0.625rem',
          fontFamily: FONT,
        },
      };
}

export function App(): JSX.Element {
  return (
    <ThemeProvider>
      <ThemedApp />
    </ThemeProvider>
  );
}

function ThemedApp(): JSX.Element {
  const { theme } = useTheme();
  return (
    <ClerkProvider
      publishableKey={env.clerkPublishableKey}
      afterSignOutUrl="/sign-in"
      appearance={clerkAppearance(theme)}
    >
      <QueryClientProvider client={queryClient}>
        <ImpersonationProvider>
          <RouterProvider router={router} />
        </ImpersonationProvider>
        <Toaster position="top-right" theme={theme} closeButton />
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </ClerkProvider>
  );
}
